import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Wallet, CheckCircle, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { motion } from "framer-motion";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(amount);
};

const statusColors = {
  pending: "bg-amber-400/10 text-amber-400",
  approved: "bg-sky-400/10 text-sky-400",
  paid: "bg-emerald-400/10 text-emerald-400",
  deducted: "bg-zinc-400/10 text-zinc-400"
};

const statusLabels = {
  pending: "Bekliyor",
  approved: "Onaylandı",
  paid: "Ödendi",
  deducted: "Kesildi"
};

const Advances = () => {
  const [advances, setAdvances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAdvance, setEditingAdvance] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: "",
    amount: "",
    date: "",
    deduction_months: 1,
    status: "pending",
    notes: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [advancesRes, employeesRes] = await Promise.all([
        axios.get(`${API}/advances`, { withCredentials: true }),
        axios.get(`${API}/employees`, { withCredentials: true })
      ]);
      setAdvances(advancesRes.data);
      setEmployees(employeesRes.data);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
        deduction_months: parseInt(formData.deduction_months)
      };
      
      if (editingAdvance) {
        await axios.put(`${API}/advances/${editingAdvance.id}`, data, { withCredentials: true });
        toast.success("Avans güncellendi");
      } else {
        await axios.post(`${API}/advances`, data, { withCredentials: true });
        toast.success("Avans eklendi");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu avans kaydını silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API}/advances/${id}`, { withCredentials: true });
      toast.success("Avans silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const openEditDialog = (advance) => {
    setEditingAdvance(advance);
    setFormData({
      employee_id: advance.employee_id,
      amount: advance.amount.toString(),
      date: advance.date,
      deduction_months: advance.deduction_months,
      status: advance.status,
      notes: advance.notes || ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingAdvance(null);
    setFormData({
      employee_id: "",
      amount: "",
      date: "",
      deduction_months: 1,
      status: "pending",
      notes: ""
    });
  };

  const filteredAdvances = advances.filter(a => 
    a.employee_name?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPending = advances.filter(a => a.status !== 'deducted').reduce((sum, a) => sum + a.amount - a.deducted_amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="advances-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-zinc-50">Avans Takibi</h1>
          <p className="text-zinc-500 text-sm mt-1">
            Toplam Bekleyen: <span className="text-amber-400">{formatCurrency(totalPending)}</span>
          </p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
          data-testid="add-advance-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Yeni Avans
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input 
          placeholder="Personel ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-zinc-900 border-zinc-800"
          data-testid="advance-search"
        />
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Personel</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Tarih</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Tutar</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Kesilen</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Kalan</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Taksit</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Durum</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredAdvances.map((advance, index) => (
                <motion.tr 
                  key={advance.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => openEditDialog(advance)}
                  className="border-b border-zinc-800 hover:bg-zinc-800/50 transition-colors cursor-pointer"
                  data-testid={`advance-row-${advance.id}`}
                >
                  <td className="px-4 py-4">
                    <p className="text-sm text-zinc-50">{advance.employee_name}</p>
                  </td>
                  <td className="px-4 py-4 text-sm text-zinc-400">
                    {advance.date}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="text-sm font-mono text-zinc-50">{formatCurrency(advance.amount)}</span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="text-sm font-mono text-emerald-400">{formatCurrency(advance.deducted_amount || 0)}</span>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="text-sm font-mono text-amber-400">
                      {formatCurrency(advance.amount - (advance.deducted_amount || 0))}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className="text-sm text-zinc-400">{advance.deduction_months} ay</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex justify-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs ${statusColors[advance.status]}`}>
                        {advance.status === 'paid' || advance.status === 'deducted' ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                        {statusLabels[advance.status]}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Pencil className="w-3 h-3 text-zinc-500" />
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); handleDelete(advance.id); }}
                        className="hover:bg-rose-400/10 text-zinc-400 hover:text-rose-400"
                        data-testid={`delete-advance-${advance.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </motion.tr>
              ))}
              {filteredAdvances.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-zinc-500">
                    {search ? "Sonuç bulunamadı" : "Henüz avans kaydı eklenmemiş"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 font-outfit">
              {editingAdvance ? "Avans Düzenle" : "Yeni Avans"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-zinc-400">Personel *</Label>
                <Select 
                  value={formData.employee_id} 
                  onValueChange={(v) => setFormData({ ...formData, employee_id: v })}
                >
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="advance-employee-select">
                    <SelectValue placeholder="Personel seçin" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {employees.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Tutar (₺) *</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="advance-amount-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Tarih *</Label>
                <Input 
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="advance-date-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Taksit Sayısı (ay)</Label>
                <Input 
                  type="number"
                  min="1"
                  value={formData.deduction_months}
                  onChange={(e) => setFormData({ ...formData, deduction_months: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="advance-months-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Durum</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(v) => setFormData({ ...formData, status: v })}
                >
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="advance-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="pending">Bekliyor</SelectItem>
                    <SelectItem value="approved">Onaylandı</SelectItem>
                    <SelectItem value="paid">Ödendi</SelectItem>
                    <SelectItem value="deducted">Tamamen Kesildi</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-400">Notlar</Label>
                <Textarea 
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  rows={2}
                  data-testid="advance-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="border-zinc-700 hover:bg-zinc-800"
              >
                İptal
              </Button>
              <Button 
                type="submit"
                className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
                data-testid="save-advance-btn"
              >
                {editingAdvance ? "Güncelle" : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Advances;
